import { Box, Button, Center, Flex, HStack, IconButton, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { useMemo, useRef } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { formatCurrency } from '@duke-hq/lib/numberFormats';
import { useMonthNavigation, type SortFilters } from '../hooks/useMonthNavigation';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ORDER_BY = 'total_spent';

type BalancesType =
  RouterOutputs['groupRewards']['listContributorsLeaderboardWithInfiniteScroll']['items'];

function useContributorsBalances(filters: SortFilters, currentLimit: number) {
  const queryResult =
    trpcTenant.groupRewards.listContributorsLeaderboardWithInfiniteScroll.useInfiniteQuery(
      { filters, limit: currentLimit },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        placeholderData: (prev) => prev,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    );

  const balances = useMemo(
    () => queryResult.data?.pages.flatMap((page) => page.items) ?? [],
    [queryResult.data],
  );

  return {
    ...queryResult,
    balances,
  };
}

function ContributorsLeaderboardRow({ balance }: { balance: BalancesType[number] }) {
  const auth = useAuthTenant();

  return (
    <HStack
      spacing={4}
      width="full"
      align="center"
      justify="space-between"
      p={2}
      bg={auth.user.drn === balance.userDrn ? '#319795' : 'gray.100'}
      borderRadius="md"
      gap={2}
    >
      <HStack spacing={2} align="center">
        <Box
          width="28px"
          height="28px"
          borderRadius="50%"
          bg={auth.user.drn === balance.userDrn ? 'white' : '#319795'}
          color={auth.user.drn === balance.userDrn ? '#319795' : 'white'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="bold"
        >
          {balance.rank}
        </Box>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={auth.user.drn === balance.userDrn ? 'white' : 'inherit'}
        >
          {[balance.firstName ?? '', balance.lastName ?? ''].filter(Boolean).join(' ')}{' '}
        </Text>
      </HStack>
      <Text
        fontSize="sm"
        fontWeight="bold"
        color={auth.user.drn === balance.userDrn ? 'white' : 'inherit'}
      >
        {formatCurrency({ number: balance.spent ?? 0, maxFractionDigits: 0 })}
      </Text>
    </HStack>
  );
}

export function ContributorsLeaderboardWidget() {
  const { filters, displayMonth, canGoNext, goToPreviousMonth, goToNextMonth } =
    useMonthNavigation(DEFAULT_ORDER_BY);

  const currentLimit = DEFAULT_PAGE_SIZE;
  const maxLoadedItemsRef = useRef(DEFAULT_PAGE_SIZE);

  const { balances, error } = useContributorsBalances(filters, currentLimit);

  const maxLoadedItems = Math.max(maxLoadedItemsRef.current, balances.length);
  maxLoadedItemsRef.current = maxLoadedItems;

  if (error) {
    return (
      <Center p={8}>
        <Text color="red.500" textAlign="center">
          <Trans>Error loading data. Please try again.</Trans>
        </Text>
      </Center>
    );
  }

  return (
    <Box bg="bg.surface" px={4} pb={4} borderRadius="md" boxShadow="sm">
      <Text fontSize="xl" fontWeight="semibold" color="fg.muted" whiteSpace="nowrap">
        <Trans>Contributors Leaderboard</Trans>
      </Text>
      <Flex mt={2} align="center" direction="column" w="100%" gap={2}>
        <Box w="100%">
          <HStack mt={2} mb={4} w="full" gap={2} align="center" justify="space-between">
            <Text fontSize="sm" fontWeight="medium" color="fg.muted">
              {displayMonth}
            </Text>
            <HStack spacing={1} ml="auto">
              <IconButton
                size="xs"
                bg="gray.100"
                aria-label="Previous month"
                color="fg.default"
                icon={<ChevronLeft size={12} />}
                onClick={goToPreviousMonth}
              />
              <IconButton
                size="xs"
                bg="gray.100"
                color={canGoNext ? 'fg.default' : 'fg.muted'}
                aria-label="Next month"
                icon={<ChevronRight size={12} />}
                onClick={goToNextMonth}
                isDisabled={!canGoNext}
              />
            </HStack>
          </HStack>
        </Box>
        {Array.isArray(balances) && balances.length > 0 ? (
          <>
            {balances.map((balance) => (
              <ContributorsLeaderboardRow balance={balance} key={balance.userDrn} />
            ))}
            <Box mt={2} w="full">
              <Button
                key="canManageCauses"
                size="md"
                w="100%"
                mt={2}
                variant="outline"
                as={Link}
                to={`/group_rewards/settings`}
              >
                <Trans>View Leaderboard</Trans>
              </Button>
            </Box>
          </>
        ) : (
          <Center p={8}>
            <Text textAlign="center">
              <Trans>No sites found</Trans>
            </Text>
          </Center>
        )}
      </Flex>
    </Box>
  );
}
